import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  getLatestTaxComputations,
  runTaxAction,
} from "../../Services/taxService";
import "./tax.css";

export default function TaxDashboard() {
  const { companyId } = useCompany();

  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const loadData = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setMsg("");

    try {
      const data = await getLatestTaxComputations(companyId);
      setRows(data || []);
    } catch (err) {
      setMsg(err.message || "Failed to load tax dashboard");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const summary = useMemo(() => {
    let vat = 0;
    let whtPayable = 0;
    let whtReceivable = 0;
    let cit = 0;
    let levy = 0;

    for (const r of rows) {
      const t = r.totals_json || {};
      if (r.tax_type_code === "VAT") vat += Number(t.vat_payable || 0);
      if (r.tax_type_code === "WHT") {
        whtPayable += Number(t.wht_payable || 0);
        whtReceivable += Number(t.wht_receivable || 0);
      }
      if (r.tax_type_code === "CIT") cit += Number(t.cit_liability || 0);
      if (r.tax_type_code === "DEV_LEVY") levy += Number(t.levy_amount || 0);
    }

    return {
      vat,
      whtPayable,
      whtReceivable,
      cit,
      levy,
      total: vat + whtPayable + cit + levy,
    };
  }, [rows]);

  async function handleGeneratePeriods() {
    if (!companyId) return;
    const year = new Date().getFullYear();
    setBusy(true);
    setMsg("");
    try {
      await runTaxAction({
        action: "generate_periods",
        companyId,
        year,
      });
      setMsg(`Tax periods generated for ${year}`);
    } catch (err) {
      setMsg(err.message || "Failed to generate tax periods");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tax-page">
      <div className="tax-page-header">
        <div>
          <h1>Tax Dashboard</h1>
          <p>Journal-driven overview of VAT, WHT, CIT and Development Levy.</p>
        </div>

        <div className="tax-actions">
          <button
            className="tax-btn primary"
            onClick={handleGeneratePeriods}
            disabled={busy || !companyId}
          >
            {busy ? "Working..." : "Generate Tax Periods"}
          </button>
          <button className="tax-btn" onClick={loadData} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {msg ? <div className="tax-alert">{msg}</div> : null}

      <div className="tax-kpi-grid">
        <div className="tax-card">
          <span>Total Open Tax Exposure</span>
          <strong>₦{summary.total.toLocaleString()}</strong>
        </div>

        <div className="tax-card">
          <span>VAT Payable</span>
          <strong>₦{summary.vat.toLocaleString()}</strong>
        </div>

        <div className="tax-card">
          <span>WHT Payable</span>
          <strong>₦{summary.whtPayable.toLocaleString()}</strong>
        </div>

        <div className="tax-card">
          <span>WHT Receivable</span>
          <strong>₦{summary.whtReceivable.toLocaleString()}</strong>
        </div>

        <div className="tax-card">
          <span>CIT Estimate</span>
          <strong>₦{summary.cit.toLocaleString()}</strong>
        </div>

        <div className="tax-card">
          <span>Development Levy</span>
          <strong>₦{summary.levy.toLocaleString()}</strong>
        </div>
      </div>

      <div className="tax-panel">
        <div className="tax-panel-header">
          <h2>Recent Tax Computations</h2>
        </div>

        {loading ? (
          <div className="tax-empty">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="tax-empty">No tax computations yet.</div>
        ) : (
          <div className="tax-table-wrap">
            <table className="tax-table">
              <thead>
                <tr>
                  <th>Tax Type</th>
                  <th>Status</th>
                  <th>Mode</th>
                  <th>Created</th>
                  <th>Totals</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.tax_type_code}</td>
                    <td>{row.status}</td>
                    <td>{row.computation_mode}</td>
                    <td>{new Date(row.created_at).toLocaleString()}</td>
                    <td>
                      <pre className="tax-json">
                        {JSON.stringify(row.totals_json || {}, null, 2)}
                      </pre>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
